import { DialogContent, DialogActions, Typography, Button, FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Immutable from 'immutable';
import * as React from 'react';
import { Price } from '../../../UIData/_stores/UIDataStore';
import { Fabric, FabricSize } from '../../../UIData/_data/Fabrics';
import { UserPatternStore } from '../../../UserData/_stores/UserPatternsStore';
import Dinero from 'dinero.js';
import Loader from '../../_components/Loader';
import PatternPreview from './PatternPreview';
import { Trans } from 'react-i18next';
import FabricSelectContainer from '../../_containers/FabricSelectContainer';

export type Props = {
	pattern: UserPatternStore
	fabrics?: Immutable.OrderedMap<number, Fabric>
	setOrderingStatus?: Function
	getFabrics?: Function
	updateCartItem?: (fd: FormData) => void
	loading?: boolean
	currency?: string
}

type State = {
	id_fabric: number
	id_size: number
	quantity: number
	price: Price
}


const PatternContainer = styled('div')(({ theme }) => ({
	maxHeight: '250px',
	overflow: 'auto',
	[theme.breakpoints.up('sm')]: {
		maxHeight: '350px',
		marginTop: '10px',
	},
	[theme.breakpoints.up('md')]: {
		height: '450px',
		marginTop: '10px',
		marginRight: '16px',
	}
}));

const T = ({ children, ...otherProps }: any) => <Trans ns="app" {...otherProps}>{children}</Trans>

export default class OrderPattern extends React.Component<Props, State> {
	state = { id_fabric: 0, id_size: 0, quantity: 1, price: null as Price }

	private isDisabled = () => !this.state.id_fabric || !this.state.id_size || !this.state.quantity || this.props.loading;

	constructor(props: Props) {
		super(props);
		this.state = { quantity: this.state.quantity, ...this.getDefault() };
		this.props.getFabrics();
	}

	componentDidUpdate(_prevProps: Props) {
		if (_prevProps.fabrics !== this.props.fabrics) this.setState(this.getDefault());
	}

	private getDefault = () => {
		if (!this.props.fabrics.count()) return;

		let fabric = -1;
		this.props.fabrics.forEach((value, key) => {
			if (fabric == -1) {
				fabric = key;
				return false;
			}
		});

		let size = -1;
		this.props.fabrics.get(fabric).get('sizes').forEach((value, key) => {
			if (size == -1) {
				size = key;
				return false;
			}
		});

		return {
			id_fabric: fabric,
			id_size: size,
			price: this.props.fabrics.get(fabric).get('sizes').get(size).get('price')
		};
	}

	private onAddToCart(e: React.FormEvent) {
		if (this.isDisabled()) return;

		e.preventDefault();

		let fd = new FormData();

		fd.append("id_design", this.props.pattern.get('id'));
		fd.append("id_fabric", String(this.state.id_fabric));
		fd.append("id_size", String(this.state.id_size));
		fd.append("quantity", String(this.state.quantity));
		fd.append("cart_item_type", "regular");

		this.props.updateCartItem(fd);

		const price = Dinero(this.state.price.toObject()).toUnit();
		const id = 'regular-' + this.state.id_fabric + '-' + this.state.id_size;
		const fabricName = this.props.fabrics.get(this.state.id_fabric)?.get('name');
		const fabricSize = this.props.fabrics.get(this.state.id_fabric)?.get('sizes').get(this.state.id_size)?.get('name');
		const name = 'Custom Fabric: ' + fabricName + ' | ' + fabricSize;

		window.fbq('track', 'AddToCart', {
			content_type: 'product',
			content_ids: [id],
			content_name: name,
			value: price * this.state.quantity,
			currency: this.props.currency,
			contents: [
				{
					id: id,
					quantity: this.state.quantity,
				}
			]
		});

		window.gtag('event', 'add_to_cart', {
			currency: this.props.currency,
			value: price * this.state.quantity,
			items: [
				{
					item_id: id,
					item_name: name,
					price: price,
					quantity: this.state.quantity,
				}
			]
		})
	}

	private onFabricChange = (fabric: Fabric, size: FabricSize) => {
		this.setState({
			id_fabric: this.props.fabrics.keyOf(fabric),
			id_size: fabric.sizes.keyOf(size),
			price: size.get('price')
		})
	}

	private onQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		e.preventDefault();
		let value = Number(e.target.value);
		value = Math.min(value, 9999);
		value = Math.max(value, 1);

		this.setState({ quantity: value });
	}

	render = () => {
		const contentStyle = this.props.loading ? { opacity: 0.5, pointerEvents: 'none' as 'none' } : {};
		const ready = this.props.fabrics && !!this.props.fabrics.count() && this.props.fabrics.has(this.state.id_fabric);
		return ready && <form onSubmit={this.onAddToCart.bind(this)} className="order-pattern">
			<div style={contentStyle}>
				<DialogContent>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Typography variant='h6'>Your Pattern: {this.props.pattern.get('name')}</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<PatternContainer>
								<PatternPreview
									src={'/user/patterns/pattern/' + this.props.pattern.get('id')}
									pattern={this.props.pattern}
									size={this.props.fabrics.get(this.state.id_fabric).get('sizes').get(this.state.id_size)}
									length={Math.max(1, Math.min(3, this.state.quantity))}
								/>
							</PatternContainer>
						</Grid>
						<Grid item xs={12} md={1}></Grid>
						<Grid container spacing={2} item xs={12} md={5} alignContent="flex-start">
							<FabricSelectContainer onChange={this.onFabricChange} emptyDefault displaySize addGridContainer={false} />
							<Grid item xs={12}>
                    			<InputLabel>{("Meters")}</InputLabel>
								<TextField
									style={{ width: "100px" }}
									fullWidth
									type="number"
									inputProps={{
										min: 1,
										max: 9999
									}}
									value={this.state.quantity}
									onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.onQuantityChange(e)}
								/>
								<Typography className="order-pattern--quantity-price">{this.state.price && '× ' + Dinero(this.state.price.toObject()).toFormat()}</Typography>
							</Grid>
							<Grid item xs={12}>
								<p style={{
									fontSize: '16px',
									color: '#151744',
								}}><T>Note : The preview shows up to 3 meters as this is the maximum we can guarantee to be consecutive.</T></p>

								<Button color="primary" type="submit" disabled={this.isDisabled()}>Add to Cart</Button>
								<Button color="secondary" onClick={() => this.props.setOrderingStatus(false)}>Cancel</Button>
							</Grid>
						</Grid>
					</Grid>
				</DialogContent>
			</div>
			{this.props.loading && <Loader noModal />}
		</form>
	}
}